import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as contactsApi from '@/api/contacts.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SelectorDeFuente } from '@/components/cartera/FuenteDelCliente';
import { useFuentes } from '@/hooks/useFuentes';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { validateManualClient } from '@/lib/manual-client';

/**
 * Alta MANUAL de cliente (Fase 1 trabajo manual): nombre + teléfono, sin
 * esperar a que escriba por WhatsApp. Si el teléfono ya existía, el server
 * devuelve el contacto existente (no duplica) y se avisa.
 */
const A_MANO = 'A mano';

export function NuevoClienteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  // T14: de qué base viene; si no se toca, «A mano».
  const [fuente, setFuente] = useState(A_MANO);
  const fuentes = useFuentes();
  const [error, setError] = useState<string | null>(null);

  function close() {
    setNombre('');
    setTelefono('');
    setFuente(A_MANO);
    setError(null);
    onClose();
  }

  const create = useMutation({
    mutationFn: () => contactsApi.createManualContact({ nombre, telefono, fuente: fuente.trim() || A_MANO }),
    onSuccess: ({ created }) => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['cartera'] });
      if (created) toast.success('Cliente creado');
      else toast.info('Ese teléfono ya existía: es el cliente de la lista');
      close();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo crear el cliente'),
  });

  function submit() {
    const v = validateManualClient({ nombre, telefono });
    if (!v.ok) {
      setError(v.error);
      return;
    }
    setError(null);
    create.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Nuevo cliente"
      description="Registra a mano a alguien que compró por fuera (sin conversación de WhatsApp)."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={create.isPending}>
            Crear cliente
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nombre"
          placeholder="Nombre del cliente"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
        <Input
          label="Teléfono"
          placeholder="18095550000"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
        />
        <SelectorDeFuente valor={fuente} onCambiar={setFuente} fuentes={fuentes} etiqueta="¿De qué base viene?" />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
